import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { searchIntelligence } from '@theme/search-intelligence';
import { searchTrending } from '@theme/search-trending';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * @type {IntersectionObserver[]}
   */
  #lazyObservers = [];

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.addEventListener('click', this.#handleComponentClick, { signal });
    // `close` ne remonte pas : capture pour suivre la fermeture du panneau de
    // filtres par Échap.
    this.addEventListener(
      'close',
      (event) => {
        if (event.target instanceof Element && event.target.matches('[data-search-filters-panel]')) {
          this.#closeProductFilters();
        }
      },
      { signal, capture: true }
    );
    this.addEventListener('input', this.#handleProductFilterChange, { signal });
    this.addEventListener('change', this.#handleProductFilterChange, { signal });

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('summary');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Handles tabs, trend buttons and product filters inside the predictive search.
   * @param {MouseEvent} event - The mouse event
   */
  #handleComponentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trendingButton = target.closest('[data-search-term]');
    if (trendingButton instanceof HTMLElement && trendingButton.dataset.searchTerm) {
      event.preventDefault();
      const searchTerm = trendingButton.dataset.searchTerm.trim();
      if (!searchTerm) return;

      this.refs.searchInput.value = searchTerm;
      this.refs.searchInput.focus();
      this.#showResetButton();
      this.#getSearchResults(searchTerm);
      return;
    }

    const tab = target.closest('[data-result-tab]');
    if (tab instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#setActivePanel(tab.dataset.resultTab || '');
      return;
    }

    const filtersToggle = target.closest('[data-search-filters-toggle]');
    if (filtersToggle instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#toggleProductFilters(filtersToggle);
      return;
    }

    // Clic sur le fond assombri : la cible est le <dialog> lui-même, ses enfants le remplissent.
    const filtersClose = target.matches('[data-search-filters-panel]')
      ? target
      : target.closest('[data-search-filters-close], [data-search-filters-apply]');
    if (filtersClose instanceof HTMLButtonElement || filtersClose instanceof HTMLDialogElement) {
      event.preventDefault();
      this.#closeProductFilters();
      return;
    }

    const filtersReset = target.closest('[data-search-filters-reset]');
    if (filtersReset instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#resetProductFilters();
      return;
    }

    // Clic sur un résultat : la requête a abouti, on la compte dans les
    // tendances (les chips, gérées plus haut, ne s'auto-renforcent pas).
    if (target.closest('[data-search-results] a[href]')) {
      this.#trackCurrentSearch();
    }
  };

  /**
   * Applies product filters when a filter input changes.
   * @param {Event} event - The input or change event
   */
  #handleProductFilterChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.closest('[data-search-filters-panel]')) return;

    this.#applyProductFilters(true);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    searchIntelligence.preload();
    this.#refreshTrending();

    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  /**
   * Configuration du tracking / des tendances réelles (Supabase), portée par
   * les data-attributes posés dans search-modal.liquid selon les réglages.
   * @returns {{url: string, key: string, locale: string} | null}
   */
  get #trendingConfig() {
    const { trendingUrl, trendingKey } = this.dataset;
    if (!trendingUrl || !trendingKey) return null;

    const locale = (document.documentElement.lang || 'fr').split('-')[0].toLowerCase();
    return { url: trendingUrl, key: trendingKey, locale };
  }

  /**
   * Remplace les chips curées par les vraies recherches tendances quand le
   * volume est suffisant (sinon la liste manuelle reste affichée). La chip
   * « grande taille » (lien) est toujours conservée en fin de liste.
   */
  async #refreshTrending() {
    const { trendingList } = this.refs;
    const config = this.#trendingConfig;
    if (!(trendingList instanceof HTMLElement) || !config) return;

    const terms = await searchTrending.fetchTrending(config);
    if (terms.length < 3) return;

    const anchor = trendingList.querySelector('[data-plus-size-chip]');
    trendingList.querySelectorAll('button[data-search-term]:not([data-plus-size-chip])').forEach((button) => button.remove());

    for (const term of terms.slice(0, 5)) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'search-modal__trending-term';
      button.dataset.searchTerm = term;
      button.textContent = term;
      if (anchor) {
        trendingList.insertBefore(button, anchor);
      } else {
        trendingList.append(button);
      }
    }
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll('.predictive-search-results__wrapper-products, .predictive-search-results__list')
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    // Le panneau de filtres est dans le formulaire : Échap doit fermer le panneau
    // seul, et les flèches ne doivent pas parcourir les résultats derrière lui.
    // composedPath : component.js remplace `target` par l'élément porteur de on:keydown.
    const origin = event.composedPath?.()[0];
    if (origin instanceof Element && origin.closest('[data-search-filters-panel]')) return;

    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        event.preventDefault();
        this.refs.searchInput.blur();
        break;
      }
    }
  };

  /**
   * Keeps the search experience inside the modal instead of submitting to /search.
   * @param {SubmitEvent} event - The submit event.
   */
  onSearchSubmit = (event) => {
    event.preventDefault();
    this.#trackCurrentSearch();
    this.refs.searchInput.blur();
  };

  /** Enregistre la requête courante dans les statistiques de recherche. */
  #trackCurrentSearch() {
    const config = this.#trendingConfig;
    const term = this.refs.searchInput.value.trim();
    if (config && term) {
      searchTrending.trackSearch(config, term);
    }
  }

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    if (event.target !== this.refs.searchInput) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const abortController = this.#createAbortController();
    this.#setLoading(true);

    // Couche intelligente : correction de fautes + synonymes vers le vocabulaire
    // du catalogue avant l'appel natif. Ne bloque pas si l'index n'est pas prêt.
    searchIntelligence.preload();
    const { query: effectiveTerm } = await searchIntelligence.rewriteQuery(searchTerm);
    if (abortController.signal.aborted || this.refs.searchInput.value.trim() !== searchTerm) return;

    let searchUrlString = Theme.routes.predictive_search_url;
    // Direct fallback for Arabic (unsupported by native Shopify predictive search)
    if (searchUrlString.startsWith('/ar/')) {
      searchUrlString = '/search/suggest';
    }

    const url = new URL(searchUrlString, location.origin);
    url.searchParams.set('q', effectiveTerm || searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Shopify n'inclut pas les articles de blog par défaut.
    url.searchParams.set('resources[type]', 'product,collection,page,article,query');

    const { predictiveSearchResults } = this.refs;

    const fetchAndRender = async (targetUrl) => {
      const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, targetUrl);
      if (!resultsMarkup || abortController.signal.aborted) return;

      this.#clearInjectedBlocks();
      morph(predictiveSearchResults, resultsMarkup);

      this.#setupResultsExperience();
      this.#resetScrollPositions();
      await this.#enhanceResults(searchTerm, abortController.signal);
    };

    try {
      await fetchAndRender(url);
    } catch (error) {
      if (!abortController.signal.aborted) {
        // Fallback for any other future locales that might fail with 417 or other issues
        const hasLanguagePrefix = /^\/[a-z]{2}(-[a-z]{2})?\/search\/suggest/.test(url.pathname);
        if (hasLanguagePrefix && url.pathname !== '/search/suggest') {
          const fallbackUrl = new URL(url);
          fallbackUrl.pathname = '/search/suggest';
          try {
            await fetchAndRender(fallbackUrl);
          } catch (fallbackError) {
            if (!abortController.signal.aborted) {
              console.error('Predictive search fallback failed:', fallbackError);
            }
          }
        } else {
          console.error('Predictive search failed:', error);
        }
      }
    } finally {
      if (this.#activeFetch === abortController) {
        this.#setLoading(false);
      }
    }
  }

  /**
   * @param {string} src
   * @param {string} alt
   * @param {boolean} isSecondary - Image révélée au survol.
   * @returns {HTMLImageElement}
   */
  #buildCardImage(src, alt, isSecondary) {
    const image = document.createElement('img');
    image.className = isSecondary ? 'resource-card__image resource-card__image--secondary' : 'resource-card__image';
    image.src = src;
    image.alt = alt;
    image.loading = 'lazy';
    image.decoding = 'async';
    return image;
  }

  /**
   * Construit une carte produit avec le balisage natif du thème.
   *
   * Les blocs de repli affichaient auparavant leur propre composant, juste à
   * côté des résultats natifs rendus par `snippets/resource-card.liquid` : deux
   * cartes différentes dans le même panneau, chacune avec sa grille, sa
   * typographie et sa bascule d'image au survol à maintenir en double. On émet
   * désormais exactement la même structure, donc tout vient d'une seule source
   * et le repli suit automatiquement toute évolution de `resource-card`.
   *
   * @param {{title: string, url: string, price?: string, image?: string|null, secondaryImage?: string|null, available?: boolean}} product
   * @returns {HTMLLIElement}
   */
  #buildFallbackCard(product) {
    const item = document.createElement('li');
    item.className = 'predictive-search-results__card predictive-search-results__card--product';

    const card = document.createElement('div');
    card.className = 'resource-card';
    card.dataset.resourceType = 'product';

    const link = document.createElement('a');
    link.className = 'resource-card__link';
    link.href = product.url;

    const label = document.createElement('span');
    label.className = 'visually-hidden';
    label.textContent = product.title;
    link.append(label);
    card.append(link);

    const media = document.createElement('div');
    media.className = 'resource-card__media';
    // `resource-card` dimensionne son visuel par cette variable, comme le fait
    // le rendu Liquid via `image_aspect_ratio`. Même cadrage que les cartes de
    // collection et que le reste du panneau.
    media.style.setProperty('--resource-card-aspect-ratio', '4 / 5');

    if (product.image) {
      media.append(this.#buildCardImage(product.image, product.title, false));

      // La bascule au survol est portée par le CSS de `resource-card`, via
      // `:has(.resource-card__image--secondary)`. Rien à câbler ici.
      if (product.secondaryImage) {
        media.append(this.#buildCardImage(product.secondaryImage, '', true));
      }
    }

    // Produit épuisé : le badge du site sur la photo, comme les cartes Liquid
    // (snippet `cp-sold-out-badge`, rendu dans un <template> de search-modal).
    if (product.available === false) {
      /** @type {HTMLTemplateElement | null} */
      const badgeTemplate = this.querySelector('template[data-sold-out-badge]');
      if (badgeTemplate) media.append(badgeTemplate.content.cloneNode(true));
    }

    card.append(media);

    const content = document.createElement('div');
    content.className = 'resource-card__content';

    const title = document.createElement('p');
    title.className = 'resource-card__title paragraph';
    title.textContent = product.title;
    content.append(title);

    if (product.price) {
      // Même imbrication que le snippet `price` : la typographie des cartes
      // cible `.resource-card__content .price`.
      const priceContainer = document.createElement('div');
      const priceRegular = document.createElement('div');
      priceRegular.className = 'price__regular';

      const price = document.createElement('span');
      price.className = 'price';
      price.textContent = product.price;

      priceRegular.append(price);
      priceContainer.append(priceRegular);
      content.append(priceContainer);
    }

    card.append(content);
    item.append(card);

    return item;
  }

  /**
   * Requête à intention « grande taille » (ex. la saisie « grande taille » ne
   * matche que la FAQ) : affiche les produits réellement disponibles en XL+
   * sous la barre d'onglets, avec un lien vers le catalogue pré-filtré,
   * pour rester cohérent avec la chip tendance du même nom.
   * @param {string} searchTerm - La requête d'origine saisie par l'utilisateur.
   * @param {AbortSignal} signal - Signal d'annulation de la recherche en cours.
   */
  async #renderPlusSizeBlock(searchTerm, signal) {
    const { plusSizeUrl, plusSizeValues, plusSizeTitle, plusSizeLinkLabel } = this.dataset;
    if (!plusSizeUrl || !plusSizeValues) return;
    if (!searchIntelligence.isPlusSizeQuery(searchTerm)) return;

    const matches = await searchIntelligence.plusSizeMatches(plusSizeValues, 4);
    if (signal.aborted || matches.length === 0) return;
    if (this.refs.searchInput.value.trim() !== searchTerm) return;

    const container = this.refs.predictiveSearchResults;
    const target = container.querySelector('[data-search-results]');
    if (!target || container.querySelector('.predictive-search-fallback--plus-size')) return;
    // Juste sous la barre d'onglets et hors des panneaux : visible quel que soit
    // l'onglet ouvert (Produits, Pages, FAQ).
    const nav = container.querySelector('[data-results-tabs]');

    const block = document.createElement('div');
    block.className = 'predictive-search-fallback predictive-search-fallback--plus-size';

    const title = document.createElement('h4');
    title.className = 'predictive-search-results__title';
    title.textContent = plusSizeTitle || 'Disponible en grande taille';
    block.append(title);

    const list = document.createElement('ul');
    list.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';

    for (const product of matches) {
      list.append(this.#buildFallbackCard(product));
    }

    block.append(list);

    const cta = document.createElement('a');
    cta.className = 'predictive-search-fallback__cta';
    cta.href = plusSizeUrl;
    cta.textContent = plusSizeLinkLabel || 'Voir toutes les grandes tailles';
    block.append(cta);

    if (nav) nav.after(block);
    else target.prepend(block);
  }

  /**
   * Quand la recherche native ne renvoie rien, propose les produits du catalogue
   * les plus proches de la requête (index local, épuisés inclus mais dépriorisés).
   * @param {string} searchTerm - La requête d'origine saisie par l'utilisateur.
   * @param {AbortSignal} signal - Signal d'annulation de la recherche en cours.
   */
  async #renderLocalFallback(searchTerm, signal) {
    const container = this.refs.predictiveSearchResults;
    // Le bloc grande taille répond déjà à l'intention : pas de double repli.
    if (container.querySelector('.predictive-search-fallback')) return;

    // Sélecteur en enfant direct : le panneau FAQ contient son propre élément
    // .predictive-search-results__no-results qu'il ne faut surtout pas matcher.
    const noResults = container.querySelector('[data-search-results] > .predictive-search-results__no-results');
    const countElement = container.querySelector('[data-tab-count="products"]');
    const nativeCount = noResults ? 0 : countElement ? parseInt(countElement.textContent, 10) || 0 : 0;

    // Intentions évidentes (couleur, occasion, attribut) posées par la section, snippet
    // cp-search-intent : on complète avec toute la famille, même si le natif est riche.
    const intents = this.#searchIntents();
    const hasIntent = intents.length > 0;

    // Natif déjà riche : rien à ajouter. À 3 produits ou moins, on complète :
    // « blanc » ne matche qu'un titre alors que 5 autres produits blancs
    // existent, « caftan mariée » ne sort qu'un bijou dont la description
    // mentionne les deux mots.
    if (nativeCount > 3 && !hasIntent) return;

    const handleOf = (product) =>
      product.handle || String(product.url || '').match(/\/products\/([^/?#]+)/)?.[1] || '';
    const shownHandles = new Set(
      [...container.querySelectorAll('.predictive-search-results__card--product a[href*="/products/"]')]
        .map((link) => String(link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/)?.[1] || '')
        .filter(Boolean)
    );

    // Fusion lexical + sémantique. Le lexical passe devant quand un produit
    // couvre TOUS les mots de la requête au niveau titre/type/couleur
    // (« white » doit sortir le Caftan Blanc Perle en premier même si le
    // sémantique le classe 5e) ; le sémantique ordonne le reste. Petit délai
    // anti-rafale : pas d'appel API à chaque frappe intermédiaire.
    const lexical = await searchIntelligence.localMatches(searchTerm, 6);
    const config = this.#trendingConfig;
    let semantic = [];
    // Pas d'appel sémantique quand l'intention est évidente : la famille vient de l'index local.
    if (config && !hasIntent) {
      await new Promise((resolve) => setTimeout(resolve, 450));
      if (signal.aborted || this.refs.searchInput.value.trim() !== searchTerm) return;
      semantic = await searchIntelligence.semanticSearch(config, searchTerm);
    }

    const merged = new Map();
    for (const product of lexical) {
      if (product.coverage === 1 && product.score >= 5) merged.set(handleOf(product), product);
    }
    for (const product of semantic) {
      if (!merged.has(handleOf(product))) merged.set(handleOf(product), product);
    }
    for (const product of lexical) {
      if (!merged.has(handleOf(product))) merged.set(handleOf(product), product);
    }
    let candidates = [...merged.values()].filter((product) => !shownHandles.has(handleOf(product)));

    // Intention évidente : les suggestions suivent le même filtre que les résultats
    // natifs, puis la famille complète (camel, taupe et marron ensemble ; toutes les
    // tenues de mariée).
    if (hasIntent) {
      const matching = await searchIntelligence.handlesMatching(intents, candidates.map(handleOf));
      // Disponibles seulement, comme la liste native (la boutique y masque les épuisés).
      candidates = candidates.filter((product) => matching.has(handleOf(product)) && product.available);
      const taken = new Set(candidates.map(handleOf));
      for (const product of await searchIntelligence.productsMatching(intents)) {
        if (!shownHandles.has(product.handle) && !taken.has(product.handle)) candidates.push(product);
      }
    }

    const candidateScores = await searchIntelligence.rankByRelevance(searchTerm, candidates.map(handleOf));
    const matches = candidates
      .map((product, position) => ({
        product,
        position,
        score: candidateScores.get(handleOf(product)) ?? 0,
      }))
      .sort((a, b) => b.score - a.score || a.position - b.position)
      .slice(0, hasIntent ? 8 : 4)
      .map(({ product }) => product);

    // Natif hors sujet (1-2 produits sans aucune correspondance de niveau
    // titre, ex. un bijou dont seule la description mentionne « caftan
    // mariée ») : les suggestions passent AU-DESSUS des résultats natifs.
    let aboveNative = false;
    if (nativeCount > 0 && nativeCount <= 2 && matches.length >= 3) {
      const nativeScores = await searchIntelligence.rankByRelevance(searchTerm, [...shownHandles]);
      const bestNative = Math.max(0, ...nativeScores.values());
      aboveNative = bestNative < 5;
    }

    if (signal.aborted || matches.length === 0) return;
    if (this.refs.searchInput.value.trim() !== searchTerm) return;
    if (container.querySelector('.predictive-search-fallback')) return;

    if (noResults) {
      noResults.textContent = this.dataset.curatedMessage || 'Une sélection inspirée par votre recherche.';
      noResults.classList.add('predictive-search-results__no-results--curated');
    }

    if (nativeCount === 0) {
      const status = container.querySelector('[role="status"]');
      if (status) {
        status.textContent = this.dataset.curatedMessage || 'Une sélection inspirée par votre recherche.';
      }
    }

    const fallback = document.createElement('div');
    fallback.className = 'predictive-search-fallback';

    const title = document.createElement('h4');
    title.className = 'predictive-search-results__title';
    title.textContent = this.dataset.fallbackTitle || 'Vous cherchiez peut-être';
    fallback.append(title);

    const list = document.createElement('ul');
    list.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';

    for (const product of matches) {
      list.append(this.#buildFallbackCard(product));
    }

    fallback.append(list);
    // Placement : au-dessus du natif quand il est hors sujet, sinon sous la
    // liste de produits native, sinon après « aucun résultat », sinon
    // au-dessus des onglets.
    const productsSection = container.querySelector('.predictive-search-results__products');
    if (nativeCount > 0 && productsSection && aboveNative) {
      productsSection.before(fallback);
    } else if (nativeCount > 0 && productsSection) {
      productsSection.after(fallback);
    } else if (noResults && noResults.parentElement) {
      noResults.after(fallback);
    } else {
      container.querySelector('[data-search-results]')?.prepend(fallback);
    }
  }

  /**
   * Retire les cartes accessoires (bijoux, ceintures) d'un bloc de résultats.
   * @param {ParentNode} root
   */
  #removeAccessoryCards(root) {
    const accessory = /\/products\/(boucles-d-oreilles|ceinture-|collier-|broche-|couronne-)/i;
    root.querySelectorAll('.predictive-search-results__card--product').forEach((card) => {
      const href = card.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
      if (accessory.test(href)) card.remove();
    });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  /**
   * Affiche l'état d'attente jusqu'à la fin des suggestions intelligentes.
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const { loadingState, predictiveSearchResults, searchInput } = this.refs;

    this.classList.toggle('is-loading', isLoading);
    if (loadingState instanceof HTMLElement) loadingState.hidden = !isLoading;
    predictiveSearchResults?.setAttribute('aria-busy', String(isLoading));
    searchInput?.setAttribute('aria-busy', String(isLoading));
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.#setLoading(false);

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      // Les accessoires (boucles ~12–25 €) ne doivent pas remplacer les
      // meilleures ventes caftans dans l'overlay de recherche.
      this.#removeAccessoryCards(recentlyViewedProductsHtml);
      const remainingCards = recentlyViewedProductsHtml.querySelectorAll(
        '.predictive-search-results__card--product'
      );
      if (remainingCards.length > 0) {
        for (const child of recentlyViewedProductsHtml.children) {
          if (child instanceof HTMLElement) {
            child.setAttribute('ref', 'recentlyViewedWrapper');
          }
        }

        const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
        if (!collectionElement) return;
        collectionElement.replaceChildren(...recentlyViewedProductsHtml.children);
      }
    }

    if (abortController.signal.aborted) return;

    this.#clearInjectedBlocks();
    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#setupResultsExperience();
    this.#resetScrollPositions();
  };

  /**
   * Retire les blocs injectés par la couche intelligente avant chaque morph :
   * le morph est positionnel, un nœud étranger décale toute la réconciliation.
   */
  #clearInjectedBlocks() {
    this.refs.predictiveSearchResults.querySelectorAll('.predictive-search-fallback').forEach((node) => node.remove());
  }

  /**
   * Améliorations post-rendu (re-ranking, intention grande taille, repli
   * local). Blindé : aucune erreur ici ne doit casser la recherche native.
   * @param {string} searchTerm - La requête d'origine saisie par l'utilisateur.
   * @param {AbortSignal} signal - Signal d'annulation de la recherche en cours.
   */
  async #enhanceResults(searchTerm, signal) {
    try {
      await this.#rerankProducts(searchTerm, signal);
      await this.#renderPlusSizeBlock(searchTerm, signal);
      await this.#renderLocalFallback(searchTerm, signal);
    } catch (error) {
      console.warn('Search enhancements skipped:', error);
    }
  }

  /**
   * Réordonne les cartes produits natives par pertinence locale : les
   * correspondances de titre passent devant celles trouvées via la
   * description (ex. « caftan » classe les caftans avant les ceintures
   * dont la description mentionne le mot), produits en stock favorisés.
   * @param {string} searchTerm - La requête d'origine saisie par l'utilisateur.
   * @param {AbortSignal} signal - Signal d'annulation de la recherche en cours.
   */
  async #rerankProducts(searchTerm, signal) {
    const container = this.refs.predictiveSearchResults;
    const lists = container.querySelectorAll('.predictive-search-results__wrapper-products');

    const handleOf = (card) =>
      card
        .querySelector('a[href*="/products/"]')
        ?.getAttribute('href')
        ?.match(/\/products\/([^/?#]+)/)?.[1] || '';

    for (const list of lists) {
      const cards = [...list.children].filter(
        (child) => child instanceof HTMLElement && child.classList.contains('predictive-search-results__card--product')
      );
      if (cards.length < 2) continue;

      const scores = await searchIntelligence.rankByRelevance(searchTerm, cards.map(handleOf));
      if (signal.aborted || scores.size === 0) return;
      if (this.refs.searchInput.value.trim() !== searchTerm) return;

      // Un produit qui porte toutes les intentions (« caftan blanc dentelle » : blanc ET
      // dentelle) passe devant ceux qui n'en portent qu'une, même si le mot est dans leur titre.
      const intents = this.#searchIntents();
      if (intents.length > 0) {
        for (const handle of await searchIntelligence.handlesMatching(intents, [...scores.keys()])) {
          scores.set(handle, (scores.get(handle) ?? 0) + 500);
        }
      }

      const ordered = cards
        .map((card, position) => ({ card, position, score: scores.get(handleOf(card)) ?? 0 }))
        .sort((a, b) => b.score - a.score || a.position - b.position);

      const sentinel = list.querySelector(':scope > [data-lazy-sentinel]');
      for (const [index, entry] of ordered.entries()) {
        entry.card.dataset.sortIndex = index.toString();
        list.insertBefore(entry.card, sentinel);
      }
      this.#syncLazyList(list, true);
    }
  }

  /**
   * Intentions évidentes (couleur, occasion, attribut) posées par la section via
   * le snippet cp-search-intent : pour chacune, ses mots séparés par des espaces.
   * @returns {string[]}
   */
  #searchIntents() {
    const resultsRoot = this.refs.predictiveSearchResults.querySelector('[data-search-results]');
    return [
      resultsRoot?.getAttribute('data-color-intent'),
      resultsRoot?.getAttribute('data-occasion-intent'),
      resultsRoot?.getAttribute('data-attribute-intent'),
    ].filter((words) => typeof words === 'string' && words.trim() !== '');
  }

  #setupResultsExperience() {
    this.#disconnectLazyObservers();
    this.#updateFaqCounts();
    this.#activateFirstAvailablePanel();
    this.#closeProductFilters();
    this.#setupLazyLists();
    this.#applyProductFilters(false);
  }

  #updateFaqCounts() {
    const faqPanel = this.refs.predictiveSearchResults.querySelector('[data-result-panel="faq"]');
    const faqCountElement = this.refs.predictiveSearchResults.querySelector('[data-faq-count]');
    const faqTab = this.refs.predictiveSearchResults.querySelector('[data-result-tab="faq"]');
    const faqTabCount = this.refs.predictiveSearchResults.querySelector('[data-tab-count="faq"]');
    const faqCount = faqCountElement instanceof HTMLElement ? Number(faqCountElement.dataset.faqCount || 0) : 0;

    if (faqTabCount) faqTabCount.textContent = faqCount.toString();
    if (faqTab instanceof HTMLButtonElement) faqTab.disabled = faqCount === 0;
    if (faqPanel instanceof HTMLElement) faqPanel.toggleAttribute('hidden', faqCount === 0);
  }

  #activateFirstAvailablePanel() {
    const selectedTab = this.refs.predictiveSearchResults.querySelector(
      '[data-result-tab][aria-selected="true"]:not(:disabled)'
    );
    if (selectedTab instanceof HTMLElement && selectedTab.dataset.resultTab) {
      this.#setActivePanel(selectedTab.dataset.resultTab);
      return;
    }

    const firstTab = this.refs.predictiveSearchResults.querySelector('[data-result-tab]:not(:disabled)');
    if (firstTab instanceof HTMLElement && firstTab.dataset.resultTab) {
      this.#setActivePanel(firstTab.dataset.resultTab);
    }
  }

  /**
   * @param {string} panelName - The panel name to activate.
   */
  #setActivePanel(panelName) {
    if (!panelName) return;

    const nav = this.refs.predictiveSearchResults.querySelector('[data-results-tabs]');
    const targetTab = Array.from(this.refs.predictiveSearchResults.querySelectorAll('[data-result-tab]')).find(
      (tab) => tab instanceof HTMLElement && tab.dataset.resultTab === panelName
    );
    if (!(targetTab instanceof HTMLButtonElement) || targetTab.disabled) return;

    this.refs.predictiveSearchResults.querySelectorAll('[data-result-tab]').forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;

      const isActive = tab === targetTab;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.refs.predictiveSearchResults.querySelectorAll('[data-result-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      panel.hidden = panel.dataset.resultPanel !== panelName;
    });

    if (nav instanceof HTMLElement) nav.dataset.activePanel = panelName;

    const filtersToggle = this.refs.predictiveSearchResults.querySelector('[data-search-filters-toggle]');
    const filtersPanel = this.refs.predictiveSearchResults.querySelector('[data-search-filters-panel]');
    const showFilters = panelName === 'products' && !targetTab.disabled;

    if (filtersToggle instanceof HTMLButtonElement) {
      filtersToggle.hidden = !showFilters;
      if (!showFilters) filtersToggle.setAttribute('aria-expanded', 'false');
    }

    if (!showFilters) this.#closeProductFilters();

    this.#setupLazyLists();
  }

  /**
   * @param {HTMLButtonElement} toggle - The filter toggle button.
   */
  #toggleProductFilters(toggle) {
    const panel = this.refs.predictiveSearchResults.querySelector('[data-search-filters-panel]');
    if (!(panel instanceof HTMLDialogElement)) return;

    // <dialog> modal, comme le tiroir des collections : la couche supérieure
    // échappe au transform de la fenêtre de recherche et au container-type des
    // résultats, qui enfermaient le panneau sur desktop.
    const shouldOpen = !panel.open;
    if (shouldOpen) panel.showModal();
    else panel.close();
    toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    this.toggleAttribute('data-search-filters-open', shouldOpen);
    this.dialog?.refs.dialog.classList.toggle('search-modal__content--filters-open', shouldOpen);
  }

  #closeProductFilters() {
    const panel = this.refs.predictiveSearchResults.querySelector('[data-search-filters-panel]');
    const toggle = this.refs.predictiveSearchResults.querySelector('[data-search-filters-toggle]');

    if (panel instanceof HTMLDialogElement && panel.open) panel.close();
    if (toggle instanceof HTMLButtonElement) toggle.setAttribute('aria-expanded', 'false');
    this.removeAttribute('data-search-filters-open');
    this.dialog?.refs.dialog.classList.remove('search-modal__content--filters-open');
  }

  #resetProductFilters() {
    const panel = this.refs.predictiveSearchResults.querySelector('[data-search-filters-panel]');
    if (!(panel instanceof HTMLElement)) return;

    panel.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = false;
    });
    panel.querySelectorAll('select').forEach((select) => {
      if (select instanceof HTMLSelectElement) select.selectedIndex = 0;
    });
    // Curseur de prix : poignées aux extrémités, puis un `input` pour que
    // <cp-price-slider> redessine le rail et les montants.
    panel.querySelectorAll('input[type="range"]').forEach((range) => {
      if (!(range instanceof HTMLInputElement)) return;
      range.value = range.dataset.borne === 'min' ? range.min : range.max;
      range.dispatchEvent(new Event('input', { bubbles: true }));
    });

    this.#applyProductFilters(true);
  }

  /**
   * @param {boolean} resetLazy - Whether to reset lazy reveal state after filtering.
   */
  #applyProductFilters(resetLazy) {
    const productsPanel = this.refs.predictiveSearchResults.querySelector('[data-result-panel="products"]');
    const filtersPanel = this.refs.predictiveSearchResults.querySelector('[data-search-filters-panel]');
    if (!(productsPanel instanceof HTMLElement) || !(filtersPanel instanceof HTMLElement)) return;

    const availabilityValues = Array.from(
      filtersPanel.querySelectorAll('[data-product-filter="availability"]:checked')
    ).map((input) => (input instanceof HTMLInputElement ? input.value : ''));
    const minPriceInput = filtersPanel.querySelector('[data-product-filter="min-price"]');
    const maxPriceInput = filtersPanel.querySelector('[data-product-filter="max-price"]');
    const sortInput = filtersPanel.querySelector('[data-product-filter="sort"]');
    const checkedColorInputs = Array.from(
      filtersPanel.querySelectorAll('[data-product-filter="color"]:checked')
    ).filter((input) => input instanceof HTMLInputElement);
    const checkedSizeInputs = Array.from(filtersPanel.querySelectorAll('[data-product-filter="size"]:checked')).filter(
      (input) => input instanceof HTMLInputElement
    );

    // Les deux cases cochées (ou aucune) : pas de filtre de disponibilité.
    const availabilityFilter = availabilityValues.length === 1 ? availabilityValues[0] : null;
    // Poignées en unités entières de la devise ; une poignée à son extrémité ne filtre pas.
    const minPrice =
      minPriceInput instanceof HTMLInputElement && Number(minPriceInput.value) > Number(minPriceInput.min)
        ? Number(minPriceInput.value) * 100
        : 0;
    const maxPrice =
      maxPriceInput instanceof HTMLInputElement && Number(maxPriceInput.value) < Number(maxPriceInput.max)
        ? Number(maxPriceInput.value) * 100
        : Number.POSITIVE_INFINITY;
    const sortMode = sortInput instanceof HTMLSelectElement ? sortInput.value : 'relevance';
    const checkedColors = checkedColorInputs.map((input) => input.value.toLowerCase());
    const checkedSizes = checkedSizeInputs.map((input) => input.value.toLowerCase());

    const cards = Array.from(productsPanel.querySelectorAll('[data-predictive-product]')).filter(
      (card) => card instanceof HTMLElement
    );

    let visibleCount = 0;

    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      const isAvailable = card.dataset.available === 'true';
      const price = Number.parseFloat(card.dataset.price || '0');
      const colors = (card.dataset.colors || '')
        .split(',')
        .map((color) => color.trim().toLowerCase())
        .filter(Boolean);
      const sizes = (card.dataset.sizes || '')
        .split(',')
        .map((size) => size.trim().toLowerCase())
        .filter(Boolean);

      const matchesAvailability = availabilityFilter === null || isAvailable === (availabilityFilter === '1');
      const matchesPrice = price >= minPrice && price <= maxPrice;
      const matchesColors = checkedColors.length === 0 || checkedColors.some((color) => colors.includes(color));
      const matchesSizes = checkedSizes.length === 0 || checkedSizes.some((size) => sizes.includes(size));
      const isVisible = matchesAvailability && matchesPrice && matchesColors && matchesSizes;

      card.dataset.filterHidden = isVisible ? 'false' : 'true';
      if (isVisible) visibleCount += 1;
    });

    this.#sortProductCards(productsPanel, sortMode);

    const emptyMessage = productsPanel.querySelector('[data-search-filter-empty]');
    if (emptyMessage instanceof HTMLElement) emptyMessage.hidden = visibleCount > 0;

    const seeResults = filtersPanel.querySelector('[data-search-filters-apply]');
    if (seeResults instanceof HTMLElement) {
      const { labelOne, labelOther } = seeResults.dataset;
      if (labelOne && labelOther) {
        seeResults.textContent = visibleCount === 1 ? labelOne : labelOther.replace('987654', visibleCount.toString());
      }
    }

    const isFiltered =
      availabilityValues.length > 0 ||
      minPrice > 0 ||
      maxPrice !== Number.POSITIVE_INFINITY ||
      checkedColors.length > 0 ||
      checkedSizes.length > 0 ||
      sortMode !== 'relevance';
    filtersPanel.querySelector('[data-search-filters-reset]')?.classList.toggle('facets__clear-all--active', isFiltered);

    productsPanel.querySelectorAll('[data-predictive-lazy-list]').forEach((list) => {
      if (list instanceof HTMLElement) this.#syncLazyList(list, resetLazy);
    });
  }

  /**
   * @param {HTMLElement} productsPanel - The active products panel.
   * @param {string} sortMode - Sort mode selected in the filters panel.
   */
  #sortProductCards(productsPanel, sortMode) {
    const list = productsPanel.querySelector('[data-predictive-lazy-list]');
    if (!(list instanceof HTMLElement)) return;

    const sentinel = list.querySelector('[data-lazy-sentinel]');
    const cards = Array.from(list.querySelectorAll('[data-predictive-product]')).filter(
      (card) => card instanceof HTMLElement
    );

    cards.sort((cardA, cardB) => {
      if (!(cardA instanceof HTMLElement) || !(cardB instanceof HTMLElement)) return 0;

      const priceA = Number.parseFloat(cardA.dataset.price || '0');
      const priceB = Number.parseFloat(cardB.dataset.price || '0');
      const indexA = Number.parseInt(cardA.dataset.sortIndex || '0', 10);
      const indexB = Number.parseInt(cardB.dataset.sortIndex || '0', 10);

      if (sortMode === 'price-ascending') return priceA - priceB || indexA - indexB;
      if (sortMode === 'price-descending') return priceB - priceA || indexA - indexB;
      return indexA - indexB;
    });

    cards.forEach((card) => list.insertBefore(card, sentinel));
  }

  #setupLazyLists() {
    this.#disconnectLazyObservers();

    const scrollRoot = this.querySelector('.predictive-search-form__content-wrapper');

    this.refs.predictiveSearchResults.querySelectorAll('[data-predictive-lazy-list]').forEach((list) => {
      if (!(list instanceof HTMLElement) || list.closest('[data-result-panel][hidden]')) return;

      this.#syncLazyList(list, true);

      const sentinel = list.querySelector('[data-lazy-sentinel]');
      if (!(sentinel instanceof HTMLElement)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const step = Number.parseInt(list.dataset.visibleStep || '4', 10);
            const current = Number.parseInt(list.dataset.visibleCount || step.toString(), 10);
            list.dataset.visibleCount = (current + step).toString();
            this.#syncLazyList(list, false);
          }
        },
        {
          root: scrollRoot instanceof HTMLElement ? scrollRoot : null,
          rootMargin: '160px 0px',
        }
      );

      observer.observe(sentinel);
      this.#lazyObservers.push(observer);
    });
  }

  /**
   * @param {HTMLElement} list - The lazy list to sync.
   * @param {boolean} reset - Whether to reset the visible count.
   */
  #syncLazyList(list, reset) {
    const step = Number.parseInt(list.dataset.visibleStep || '4', 10);
    if (reset || !list.dataset.visibleCount) list.dataset.visibleCount = step.toString();

    const visibleLimit = Number.parseInt(list.dataset.visibleCount || step.toString(), 10);
    const items = Array.from(list.querySelectorAll('[data-lazy-item]')).filter((item) => item instanceof HTMLElement);
    const candidates = items.filter((item) => item instanceof HTMLElement && item.dataset.filterHidden !== 'true');

    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const candidateIndex = candidates.indexOf(item);
      item.hidden = item.dataset.filterHidden === 'true' || candidateIndex < 0 || candidateIndex >= visibleLimit;
    });

    const sentinel = list.querySelector('[data-lazy-sentinel]');
    if (sentinel instanceof HTMLElement) sentinel.hidden = candidates.length <= visibleLimit;
  }

  #disconnectLazyObservers() {
    this.#lazyObservers.forEach((observer) => observer.disconnect());
    this.#lazyObservers = [];
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
